import React, { memo, useEffect, useMemo, useState } from "react";
import { Button } from "components/Buttons/Button";
import { ButtonType } from "types/ui";
import { USER_GROUPS, userOperations } from "constants/roles";
import { ManagementType, StatusENUM } from "types/common";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { getSelectedClinician } from "store/ClinicianStore/actions";
import { formatName } from "./common/functions";
import {
  selectCurrentClinician,
  selectCurrentClinicTeamMembersList,
  selectCurrentClinicTeamMembersStringList,
} from "store/ClinicianStore/selectors";
import {
  selectClinicPatientsOffset,
  selectLoading,
} from "store/PatientStore/selectors";
import {
  assignTeamLeadToPatient,
  assignTeamMembersToPatient,
  getClinicPatientsList,
} from "store/PatientStore/actions";
import { AssignModalBox as AssignTeamLeadModalBox } from "./components/AssignModalBox";
import { MultiSelect } from "../../../../components/SportSelect/MultiSelect";
import { darkButton, lightButton } from "./common/styles";
import { Clinician } from "types/Clinician";
import { getRouteLink, getYearsFromUtc } from "utils/general";
import { useHistory } from "react-router-dom";
import { PAGES } from "constants/pages";
import {
  setLimitClinicPatients,
  setOffsetClinicPatients,
  setTotalClinicPatients,
} from "store/PatientStore/patientSlice";
import { CLINICIAN_PATIENT_LIMIT } from "constants/dashboard";
import { ClinicAssitantRow } from "./rowsByRole/ClinicAssistantRow";
import moment from "moment";
import { StatusBall } from "../../../../components/StatusBall/StatusBall";
import { FloatingMenu } from "../../../../components/FloatingMenu/FloatingMenu";
import { getDates } from "../../../../utils/helpers";
import { icons } from "constants/icons";
import { ClinicRepository } from "repositories/ClinicRepository";
import { UserDetailsModal } from "../../../../components/UserDetailsModal/UserDetailsModal";

type Props = {
  managementType?: ManagementType;
  openCreateEditModalOperation?: any;
  user?: any;
};
export const ManagementTableRow = memo(
  ({ user, managementType, openCreateEditModalOperation }: Props) => {
    const dispatch = useAppDispatch();
    const [openTeamLeadDialog, setOpenTeamLeadDialog] = useState(false);
    const [isButtonDarkMode, setIsButtonDarkMode] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [formattedRoles, setFormattedRoles] = useState<string[]>([]);
    const [userStatus, setUserStatus] = useState<string>(user.status);
    const [isRowLoading, setIsRowLoading] = useState<boolean>(false);
    const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
    const currentClinician = useAppSelector(selectCurrentClinician);
    const currentClinicTeamMembersList: any = useAppSelector(
      selectCurrentClinicTeamMembersList
    );
    const clinicRepository = ClinicRepository.getInstance();

    const { parsedDates: dates, lastInviteOlderThat1Week } = getDates(
      user.profileInfo?.inviteDates
    );

    const isInviteExpired =
      lastInviteOlderThat1Week &&
      user.profileInfo.authStatus === StatusENUM.invited;
    const getStatus = (capitalize?: boolean) => {
      const statusString = user.profileInfo.authStatus || "";
      if (!statusString) {
        return "";
      }

      const statusStringParsed = isInviteExpired
        ? StatusENUM.expired
        : statusString;

      if (capitalize) {
        const firstLeter = statusStringParsed.charAt(0)?.toUpperCase();
        const word = statusStringParsed.slice(1, statusStringParsed.length);
        return `${firstLeter}${word}`;
      } else {
        return statusStringParsed;
      }
    };

    const actions = useMemo(() => {
      const options = [];
      if (managementType == ManagementType.USER) {
        if (isInviteExpired) {
          options.push({
            text: "Send another invite",
            handler: () => {
              dispatch(getSelectedClinician(user?._id));
              openCreateEditModalOperation(true, userOperations.INVITE);
            },
          });
        }
        if (user.profileInfo.authStatus === StatusENUM.registered) {
          options.push({
            text: "Edit",
            handler: () => {
              dispatch(getSelectedClinician(user?._id));
              openCreateEditModalOperation(true, userOperations.EDIT);
            },
          });
        }
        if (userStatus !== "disabled") {
          options.push({
            text: "Disable",
            handler: () => {
              dispatch(getSelectedClinician(user?._id));
              openCreateEditModalOperation(true, userOperations.DELETE);
            },
          });
        }
        if (userStatus === "disabled") {
          options.push({
            text: "Enable",
            handler: async () => {
              setIsRowLoading(true);
              await clinicRepository.enableClinicianFromCognito(user?._id);
              setUserStatus("active");
              setIsRowLoading(false);
            },
          });
        }
      }
      return options;
    }, [user.profileInfo.authStatus, userStatus]);

    const currentClinicTeamMembersStringList: any = useAppSelector(
      selectCurrentClinicTeamMembersStringList
    );
    const patientLoading: any = useAppSelector(selectLoading);
    const [memberOptions, setMemberOptions] = useState<string[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [currentUserRolesHeader] = useState(currentClinician?.role);
    const history = useHistory();
    const currentClinicPatientsOffset = useAppSelector(
      selectClinicPatientsOffset
    );

    useEffect(() => {
      if (user?.role && Array.isArray(user?.role) && user?.role?.length > 0) {
        const newFormattedRoles = user.role.map(formatName);
        setFormattedRoles(newFormattedRoles);
      }
      if (
        user?.assignedTeamMembers &&
        Array.isArray(user?.assignedTeamMembers) &&
        user?.assignedTeamMembers?.length > 0
      ) {
        const userTeamMemberNames: string[] = [];
        const userTeamMemberIds: string[] = [];
        user?.assignedTeamMembers?.forEach((element: Clinician) => {
          userTeamMemberNames.push(
            element.profileInfo?.firstName + " " + element.profileInfo?.lastName
          );
          userTeamMemberIds.push(element._id);
        });
        setMemberOptions(userTeamMemberNames);
        setSelectedMembers(userTeamMemberIds);
      }
    }, [user]);

    const updateUI = () => {
      dispatch(setOffsetClinicPatients(0));
      dispatch(setTotalClinicPatients(0));
      dispatch(getClinicPatientsList());
    };

    const assignTeamLead = async (teamLead: string | null) => {
      dispatch(
        setLimitClinicPatients(
          currentClinicPatientsOffset + CLINICIAN_PATIENT_LIMIT
        )
      );
      dispatch(assignTeamLeadToPatient(user?._id, teamLead, updateUI));
      setOpenTeamLeadDialog(false);
    };

    const assignTeamMembers = async (teamMembers: string[]) => {
      const limit = currentClinicPatientsOffset + CLINICIAN_PATIENT_LIMIT;
      dispatch(setLimitClinicPatients(limit < 0 ? 5 : limit));
      dispatch(assignTeamMembersToPatient(user?._id, teamMembers, updateUI));
      setIsButtonDarkMode(false);
    };

    const addToSelectedMembers = (value: string) => {
      const matchingMember: Clinician | undefined = currentClinicTeamMembersList
        ?.slice()
        .find((e: Clinician) => {
          return (
            e?.profileInfo?.firstName + " " + e?.profileInfo?.lastName === value
          );
        });
      if (matchingMember) {
        setMemberOptions([...memberOptions, value]);
        setSelectedMembers([...selectedMembers, matchingMember._id]);
      }
    };

    const removeFromSelectedMembers = (value: string) => {
      const matchingMember: Clinician | undefined = currentClinicTeamMembersList
        ?.slice()
        .find((e: Clinician) => {
          return (
            e?.profileInfo?.firstName + " " + e?.profileInfo?.lastName === value
          );
        });
      if (matchingMember) {
        setMemberOptions(memberOptions.filter((item) => item !== value));
        setSelectedMembers(
          selectedMembers.filter((item) => item !== matchingMember._id)
        );
      }
    };

    const resetFilter = () => {
      setMemberOptions([]);
      setSelectedMembers([]);
    };

    const onItemSelected = (e: any, name: string) => {
      if (e.target.checked) {
        addToSelectedMembers(name);
      } else {
        removeFromSelectedMembers(name);
      }
    };

    const setAnchor = (element: null | HTMLElement) => {
      setAnchorEl(element);
    };

    const handleClickTeamLead = () => {
      setOpenTeamLeadDialog(true);
    };

    const clickHandler = () => {
      if (managementType === ManagementType.USER) {
        setOpenDetailsModal(true);
      }
      if (managementType == ManagementType.PATIENT_CARE) {
        history.push(`${getRouteLink(PAGES.patientDetails)}${user?._id}`);
      }
    };
    const renderManagementType = (
      managementType: any,
      currentUserRolesHeader: any,
      user: any
    ) => {
      if (
        (managementType === ManagementType.PATIENT ||
          managementType === ManagementType.USER) &&
        currentUserRolesHeader
      ) {
        if (
          currentUserRolesHeader.includes(USER_GROUPS.CLINIC_ADMIN) &&
          managementType === ManagementType.USER
        ) {
          return (
            <>
              <div className="w-[250px] flex">
                <p>{formattedRoles.join(", ")}</p>
              </div>

              <div className="w-[200px] flex">
                {dates?.length ? (
                  <p>
                    {dates
                      .map((date: string) => moment(date).format("L"))
                      .join("; ")}
                  </p>
                ) : (
                  <p>-</p>
                )}
              </div>
              <div
                className="w-[150px] capitalize text-center"
                style={{ display: "flex", alignItems: "center" }}
              >
                <StatusBall status={getStatus()} />
                <p>{getStatus(true)}</p>
              </div>
            </>
          );
        } else if (
          managementType === ManagementType.PATIENT &&
          currentUserRolesHeader.includes(USER_GROUPS.CLINIC_ASSISTANT)
        ) {
          return (
            <div className="w-[200px] flex">
              {
                <p>
                  {user?.assignedTeamLead?.length > 0
                    ? user?.assignedTeamLead
                        .map(
                          (e: any) =>
                            e.profileInfo?.firstName +
                            " " +
                            e.profileInfo?.lastName
                        )
                        .join(", ")
                    : "No team lead"}
                </p>
              }
            </div>
          );
        } else {
          return (
            <div
              className="w-[200px] flex"
              aria-hidden="true"
              onClick={() => {
                setOpenDetailsModal(true);
              }}
            >
              {
                <p>
                  {user?.assignedTeamMembers?.length > 0
                    ? user?.assignedTeamMembers
                        .map(
                          (e: any) =>
                            e.profileInfo?.firstName +
                            " " +
                            e.profileInfo?.lastName
                        )
                        .join(", ")
                    : "No team members"}
                </p>
              }
            </div>
          );
        }
      } else {
        return null;
      }
    };

    const renderLastExamMinResult = (user: any) => {
      const { lastExamItem } = user;
      if (lastExamItem?.joint?.side === "right") {
        const examResult = lastExamItem?.examMLResult;
        return examResult
          ? (lastExamItem?.examMLResult?.rightMinValue ?? 0).toFixed(2)
          : "-";
      } else {
        const examResult = lastExamItem?.examMLResult;
        return examResult
          ? (lastExamItem?.examMLResult?.leftMinValue ?? 0).toFixed(2)
          : "-";
      }
    };

    const renderLastExamMaxResult = (user: any) => {
      const { lastExamItem } = user;
      if (lastExamItem?.joint?.side === "right") {
        const examResult = lastExamItem?.examMLResult;
        return examResult
          ? (lastExamItem?.examMLResult?.rightMaxValue ?? 0).toFixed(2)
          : "-";
      } else {
        const examResult = lastExamItem?.examMLResult;
        return examResult
          ? (lastExamItem?.examMLResult?.leftMaxValue ?? 0).toFixed(2)
          : "-";
      }
    };

    return (
      <>
        <div
          className="group w-full min-h-[54px] card rounded-xl flex items-center
        justify-between font-normal text-grayDark hover:bg-bgDark hover:text-white hover:shadow-xl"
          onClick={clickHandler}
          aria-hidden="true"
          onMouseEnter={() => setIsButtonDarkMode(true)}
          onMouseLeave={() => setIsButtonDarkMode(false)}
        >
          {managementType != ManagementType.PATIENT_CARE && (
            <>
              <div
                className="w-[300px] flex items-center"
                style={{ marginLeft: "40px" }}
              >
                {user?.imageUrl ? (
                  <div className="rounded-full overflow-hidden">
                    <img
                      alt="user image"
                      className="object-cover min-w-full min-h-full w-[30px] h-[30px]"
                      src={user?.imageUrl || ""}
                    />
                  </div>
                ) : (
                  <div className="w-[30px] h-[30px] bg-grayDark rounded-full overflow-hidden" />
                )}
                <p
                  style={{
                    marginLeft: "8px",
                    marginRight: "16px",
                    wordWrap: "break-word",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    maxWidth: "70%",
                  }}
                >
                  {user?.profileInfo?.firstName} {user?.profileInfo?.lastName}
                </p>
              </div>
              {renderManagementType(
                managementType,
                currentUserRolesHeader,
                user
              )}
            </>
          )}
          {managementType == ManagementType.PATIENT_CARE && (
            <>
              <div className="w-[250px] flex items-center space-x-3 ">
                <div className="w-[30px] h-[30px] bg-grayDark rounded-full overflow-hidden">
                  <img
                    alt="user image"
                    className="object-cover min-w-full min-h-full"
                    src={user?.imageUrl || ""}
                  />
                </div>
                <p
                  style={{
                    wordWrap: "break-word",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    maxWidth: "100%",
                  }}
                >
                  {user?.profileInfo?.firstName} {user?.profileInfo?.lastName}
                </p>
              </div>
              <div className="w-[100px] flex">
                {
                  <p>
                    {user?.profileInfo?.dateOfBirth
                      ? getYearsFromUtc(user?.profileInfo?.dateOfBirth)
                      : "-"}
                  </p>
                }
              </div>
              <div className="w-[100px] flex">
                {<p>{renderLastExamMinResult(user)}</p>}
              </div>
              <div className="w-[100px] flex">
                {renderLastExamMaxResult(user)}
              </div>
              <div className="w-[100px] flex">
                {<p>{user?.lastExamItem?.outcomeScore ?? "-"}</p>}
              </div>
              <div className="w-[100px] flex">
                {<p>{user?.lastExamItem?.painScaleValue ?? "-"}</p>}
              </div>
              <div className="w-[100px] flex">{<p>{"N/A"}</p>}</div>
              <div className="w-[200px] flex">
                {
                  <p
                    style={user?.dailyCheckInCount < 16 ? { color: "red" } : {}}
                  >
                    {user?.dailyCheckInCount ?? "-"}
                  </p>
                }
              </div>
              <div className="w-[200px] flex">
                {
                  <p>
                    {user?.lastExamItem?.joint?.side === "right"
                      ? user?.lastExamItem?.examMLResult
                        ? (
                            ((user?.lastExamItem?.examMLResult?.rightMaxValue ??
                              0) -
                              (user?.lastExamItem?.examMLResult
                                ?.rightMinValue ?? 0)) as number
                          ).toFixed(2)
                        : "-"
                      : user?.lastExamItem?.examMLResult
                      ? (
                          ((user?.lastExamItem?.examMLResult?.leftMaxValue ??
                            0) -
                            (user?.lastExamItem?.examMLResult?.leftMinValue ??
                              0)) as number
                        ).toFixed(2)
                      : "-"}
                  </p>
                }
              </div>
            </>
          )}
          {managementType == ManagementType.USER && (
            <div style={{ width: "100px", display: "flex" }}>
              {userStatus === "disabled" && (
                <img alt="status" src={icons.clinicDisabledStatus} />
              )}
              <FloatingMenu options={actions} loading={isRowLoading} />
            </div>
          )}
          {managementType == ManagementType.PATIENT &&
            (currentClinician?.role?.includes(USER_GROUPS.CLINIC_ASSISTANT) ? (
              <>
                <ClinicAssitantRow
                  setOpenDetailsModal={setOpenDetailsModal}
                  user={user}
                  style={{
                    wordWrap: "break-word",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    maxWidth: "250px",
                    minWidth: "250px",
                    textAlign: "center",
                  }}
                  isButtonDarkMode={isButtonDarkMode}
                  setIsButtonDarkMode={setIsButtonDarkMode}
                />
                <div style={{ flex: 1, textAlign: "end" }}>
                  <Button
                    title="Assign"
                    onClick={handleClickTeamLead}
                    classNames="group-hover:border-white"
                    type={ButtonType.outline}
                    style={{ marginRight: 10, padding: 5 }}
                    setRef={setAnchor}
                  />
                </div>
              </>
            ) : (
              <div style={{ flex: 1, textAlign: "end" }}>
                <MultiSelect
                  options={currentClinicTeamMembersStringList}
                  selectedOptions={memberOptions}
                  onItemSelected={onItemSelected}
                  hint={"Assign"}
                  menuSubTitle={""}
                  menuTitle={"Select Clinic Team Members"}
                  leftButtonName={"Reset"}
                  onLeftButtonClicked={resetFilter}
                  rightButtonName={"OK"}
                  onRightButtonClicked={() => {
                    assignTeamMembers(selectedMembers);
                  }}
                  onSelectOpened={() => {
                    setIsButtonDarkMode(false);
                  }}
                  style={isButtonDarkMode ? darkButton : lightButton}
                  displayIcon={false}
                  emptyMessage="No team members to select"
                  isLoading={patientLoading}
                  listItemStyle={{
                    wordWrap: "break-word",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </div>
            ))}
          <AssignTeamLeadModalBox
            open={openTeamLeadDialog}
            setOpen={setOpenTeamLeadDialog}
            anchorEl={anchorEl}
            id={user?._id}
            assignTeamLead={assignTeamLead}
          />
        </div>
        {openDetailsModal && (
          <UserDetailsModal
            isEnabled={openDetailsModal}
            setIsEnabled={setOpenDetailsModal}
            user={user}
          />
        )}
      </>
    );
  }
);
